<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import ExpectedSpendButton from '@/components/session/ui/ExpectedSpendButton.vue';
import { useKycSessionStore } from '@/stores/kycSession.store';
import FlowNavigationButton from '@/components/session/ui/FlowNavigationButton.vue';
import { AmethystClient } from '@/integrations/Immersve/AmethystClient';
import Mixpanel from '@/integrations/Mixpanel';
import KycSessionLayout from '@/layout/KycSessionLayout.vue';

const isLoading = ref(true);

const { kycSession } = storeToRefs(useKycSessionStore());

if (!kycSession.value) {
  throw new Error('Kyc session not found');
}

async function saveExpectedSpend(expectedSpendAmount: string) {
  isLoading.value = true;
  if (!kycSession.value) throw new Error('Kyc session not found');

  Mixpanel.instance.trackSpendingSpecified(expectedSpendAmount);

  await kycSession.value.cardholder.updateExpectedSpend(expectedSpendAmount);

  // complete KYC session if the user flow is partial (without KYC)
  if (!kycSession.value.steps.hasKycSteps()) {
    await new AmethystClient().completeKycSession(kycSession.value.id);
  }

  return true;
}

isLoading.value = false;
</script>

<template>
  <KycSessionLayout>
    <div class="flex flex-col" data-test="screen-kyc-spending">
      <h1 class="mb-4 text-3xl">What is your estimated monthly spending in USD?</h1>
      <b class="text-charcoal-800 mt-2 mb-1">Select amount</b>
      <ExpectedSpendButton
        :preNavigationAction="saveExpectedSpend"
        text="Less than $2,000"
        expectedSpendAmount="200000"
      />
      <ExpectedSpendButton
        :preNavigationAction="saveExpectedSpend"
        text="Between $2,000 - $4,000"
        expectedSpendAmount="400000"
      />
      <ExpectedSpendButton
        :preNavigationAction="saveExpectedSpend"
        text="Between $4,000 - $6,000"
        expectedSpendAmount="600000"
      />
      <ExpectedSpendButton
        :preNavigationAction="saveExpectedSpend"
        text="Between $6,000 - $8,000"
        expectedSpendAmount="800000"
      />
      <ExpectedSpendButton
        class="mb-10"
        :preNavigationAction="saveExpectedSpend"
        text="$8,000 or more"
        expectedSpendAmount="1000000"
      />
    </div>
    <template #button-bar>
      <FlowNavigationButton text="Back" direction="back" />
    </template>
  </KycSessionLayout>
</template>
